import * as React from 'react';
import { path, pathOr } from 'ramda';

import { IBaseQuestionProps, TAnswersState } from '../FormContainer/types/internal';
import { QuestionLayoutContainer } from '../QuestionLayoutContainer';
import { FormInputsLayout } from '../../components/FormInputsLayout';
import { useSimpleForm } from '../../utils/useSimpleForm';
import { isEmptyString, isValidLetters } from '../../utils/validators';
import { pickOnlyMeaning } from '../../utils/pickOnlyMeaning';
import { letterValidator } from './utils/letterValidator';
import { useFormTracking } from '../../utils/useFormTracking';

export const LetterQuestionContainer: React.FC<React.PropsWithChildren<IBaseQuestionProps>> = props => {
  const {
    question: { answerField, placeholder, title, required, helpText },
    answersState,
    setNextStep,
    setAnswersState,
  } = props;

  const { trackSubmit, trackChange } = useFormTracking();

  const {
    handleSubmit,
    handleChange: onChange,
    values,
    errors,
    isSubmitDisabled,
  } = useSimpleForm({
    onSubmit(values) {
      setAnswersState(prevState => {
        const value = values[answerField] ? values[answerField] : null;
        const nextAnswersState: TAnswersState = {
          ...prevState,
          [answerField]: {
            answerField,
            value,
          },
        };

        setNextStep(nextAnswersState);
        trackSubmit({
          isRequired: required,
          field: answerField,
          value: `${value}`,
        });

        return nextAnswersState;
      });
    },
    validators: {
      [answerField]: letterValidator(required),
    },
    initialValues: pickOnlyMeaning({
      [answerField]: path([answerField, 'value'], answersState),
    }),
    required,
  });

  const handleChange = React.useCallback<NonNullable<React.ComponentProps<'input'>['onChange']>>(
    e => {
      trackChange();

      const value = e.target.value;

      if (isValidLetters(value) || isEmptyString(value)) {
        onChange(answerField, e.target.value);
      }
    },
    [onChange, answerField, trackChange],
  );

  return (
    <QuestionLayoutContainer
      title={title}
      onSubmit={handleSubmit}
      description={helpText?.text}
      disableSubmit={isSubmitDisabled}
    >
      <FormInputsLayout
        InputProps={{
          type: 'text',
          name: answerField,
          invalid: !!errors[answerField],
          errorMessage: errors[answerField],
          placeholder: placeholder || '',
          onChange: handleChange,
          value: pathOr('', [answerField], values),
        }}
      />
    </QuestionLayoutContainer>
  );
};

LetterQuestionContainer.displayName = 'LetterQuestionContainer';
